import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { useMemo } from 'react'
import {
  conditionSearchParam,
  decodeConditions,
  encodeConditions,
} from '@/components/condition-filter'
import { billConditionFields } from '@/components/condition-filter/accounting-fields'
import type { BillFilters, BillStatus } from '@/features/accounting-bills/api'
import { BillList } from '@/features/accounting-bills/bill-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the bills list.
 *
 * Route definition, guard, render — nothing else, per the route-shell rule in
 * `apps/company/AGENTS.md`. `validateSearch` belongs here because the URL is a
 * route concern; what the screen does with the values is the feature's.
 *
 * The grant guard is presentation only. The API enforces
 * `accounting_bill.view` on every bill endpoint, so a stale grant still 403s
 * server-side; this only keeps a user without the grant out of a screen they
 * cannot use.
 *
 * `bills.new.tsx` and `bills.$billId.tsx` are CHILDREN of this route in flat
 * file routing, so without an Outlet they never mount and their URLs silently
 * render this list instead.
 */
export const Route = createFileRoute('/_authenticated/accounting/bills')({
  validateSearch: (search: Record<string, unknown>): BillFilters => ({
    c: conditionSearchParam(search.c),
    match: search.match === 'any' ? 'any' : undefined,
    tab: search.tab === 'bill' || search.tab === 'debit_note' ? search.tab : undefined,
    status: isBillStatus(search.status) ? search.status : undefined,
    supplier: typeof search.supplier === 'string' && search.supplier ? search.supplier : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    overdue: search.overdue === '1' || search.overdue === true ? true : undefined,
    page: typeof search.page === 'number' ? search.page : undefined,
    perPage: [10, 25, 50, 100].includes(Number(search.perPage))
      ? Number(search.perPage)
      : undefined,
    sort: isBillSort(search.sort) ? search.sort : undefined,
    direction:
      search.direction === 'asc' || search.direction === 'desc' ? search.direction : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_bill.view')) {
      throw redirect({ to: '/accounting' })
    }
  },
  component: AccountingBillsRoute,
})

function AccountingBillsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const search = Route.useSearch()
  const { conditions, match } = useMemo(
    () => decodeConditions({ c: search.c, match: search.match }, billConditionFields),
    [search.c, search.match],
  )
  const params = { ...search, conditions, match }

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <BillList
      params={params}
      onParamsChange={(next) =>
        void navigate({
          to: '/accounting/bills',
          search: {
            ...next,
            ...encodeConditions(next.conditions ?? [], next.match ?? 'all'),
            conditions: undefined,
          },
        })
      }
    />
  )
}

const BILL_STATUSES: BillStatus[] = ['draft', 'confirmed', 'partially_paid', 'paid', 'cancelled']

function isBillStatus(value: unknown): value is BillStatus {
  return typeof value === 'string' && (BILL_STATUSES as string[]).includes(value)
}

function isBillSort(value: unknown): value is NonNullable<BillFilters['sort']> {
  return ['bill_date', 'due_date', 'bill_number', 'total_amount', 'supplier'].includes(
    String(value),
  )
}
